import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { directorApi } from '@/services/api/director'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { ApiErrorResponse } from '@/services/api/client'
import type { DirectorConfig, DirectorDocumentItem, DirectorRequestDetail, DirectorTimelineEvent } from '@/types/director'
import { DECISION_TYPE_LABELS, PAYMENT_TYPE_LABELS } from '@/types/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { formatDeadline } from '@/utils/caseList'
import { documentWorkOpen } from '@/utils/documentDue'
import { avatarColorOf, initialsOf } from '@/utils/caseList'
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal'
import { QuickApproveModal } from '@/components/director/QuickApproveModal'
import { QuickRejectModal } from '@/components/director/QuickRejectModal'
import { QuickInfoModal } from './QuickInfoModal'
import { QuickReturnModal } from './QuickReturnModal'
import { CaseInfoLine } from './CaseInfoLine'
import { CaseFactsGrid } from './CaseFactsGrid'
import { CaseLessonsTable } from './CaseLessonsTable'
import { EffectivePlanView } from '@/components/effective-plan/EffectivePlanView'
import { CasePlanActions } from '@/components/effective-plan/CasePlanActions'
import { caseSentenceAbortVisible, caseWeekKeys } from '@/components/effective-plan/planFormat'
import { CaseDocumentsBlock } from './CaseDocumentsBlock'
import { CaseTimelineBlock } from './CaseTimelineBlock'
import { CaseActionFooter } from './CaseActionFooter'
import { ChevronLeft, ChevronRight, ArrowLeft, BellRing, AlertTriangle, Lock, MessageCircle } from 'lucide-react'

interface CaseDetailPaneProps {
  requestId: string | null
  config: DirectorConfig | null
  /** K2: Aktionsleiste nur für offene Fälle (selectedId ∈ pending) */
  readOnly: boolean
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  /** E5: Mobile-Overlay schliessen / Desktop deselect */
  onBack: () => void
  /** Liste refreschen (nach Aktionen/Konflikten, K7) */
  onChanged: () => void | Promise<void>
  /** Externer Refresh-Treiber (z.B. nach Listen-Refresh) */
  refreshKey?: number
}

type ActionModal = 'approve' | 'reject' | 'info' | 'return' | null

/** Fallakte v3 (B2): Cooldown aus reminder_sent — bevorzugt next_possible_at vom Backend. */
function deriveCooldown(
  events: DirectorTimelineEvent[],
  kind: 'documents' | 'info',
): string | null {
  const last = events.find(e => e.type === 'reminder_sent' && e.kind === kind)
    ?? events.find(e => {
      const marker = kind === 'documents' ? '(Dokumente)' : '(Rückfrage)'
      return e.type === 'reminder_sent' && e.title.includes(marker)
    })
  if (!last) return null
  if (last.next_possible_at) {
    const until = new Date(last.next_possible_at).getTime()
    if (Number.isNaN(until)) return null
    return until > Date.now() ? last.next_possible_at : null
  }
  if (!last.occurred_at) return null
  const sentAt = new Date(last.occurred_at).getTime()
  if (Number.isNaN(sentAt)) return null
  const until = sentAt + 24 * 3600_000
  return until > Date.now() ? new Date(until).toISOString() : null
}

function headerStatus(detail: DirectorRequestDetail): { label: string; classes: string } {
  const dt = detail.latest_decision?.decision_type
  if (dt === 'approved') return { label: 'Genehmigt', classes: 'bg-green-100 text-green-800' }
  if (dt === 'rejected') return { label: 'Abgelehnt', classes: 'bg-red-100 text-red-800' }
  if (dt === 'returned') return { label: 'Angepasst', classes: 'bg-orange-100 text-orange-800' }
  const hasFile = Boolean(detail.has_document ?? detail.documents_received)
  const docsOpen = documentWorkOpen(
    Boolean(detail.document_due), detail.documents_requested_at, hasFile,
  )
  if (docsOpen) {
    return { label: 'Dokumente offen', classes: 'bg-orange-100 text-orange-800' }
  }
  if (dt === 'info_requested') {
    return { label: 'Rückfrage offen', classes: 'bg-blue-100 text-blue-800' }
  }
  return { label: 'Bereit zur Entscheidung', classes: 'bg-green-100 text-green-800' }
}

/** Fallakte v2 (P5.2): Detail-Composition mit Header, Blöcken und fixer Aktionsleiste. */
export function CaseDetailPane({
  requestId, config, readOnly, hasPrev, hasNext, onPrev, onNext, onBack, onChanged, refreshKey,
}: CaseDetailPaneProps) {
  const navigate = useNavigate()
  const [detail, setDetail] = useState<DirectorRequestDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [timeline, setTimeline] = useState<DirectorTimelineEvent[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineError, setTimelineError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [errorNotice, setErrorNotice] = useState<string | null>(null)
  const [modal, setModal] = useState<ActionModal>(null)
  const [previewDoc, setPreviewDoc] = useState<DirectorDocumentItem | null>(null)
  const [requestingDocs, setRequestingDocs] = useState(false)
  const [cancellingDocs, setCancellingDocs] = useState(false)
  const [reminding, setReminding] = useState(false)
  const [remindCooldownUntil, setRemindCooldownUntil] = useState<string | null>(null)
  const [infoRemindCooldownUntil, setInfoRemindCooldownUntil] = useState<string | null>(null)
  const [payerSaving, setPayerSaving] = useState(false)
  const [aborting, setAborting] = useState(false)
  const [planTick, setPlanTick] = useState(0)

  const loadDetail = useCallback(async () => {
    if (!requestId) {
      setDetail(null)
      setTimeline([])
      setDetailError(null)
      return
    }
    setDetailLoading(true)
    setDetailError(null)
    try {
      const d = await directorApi.getRequestDetail(requestId)
      setDetail(d)
    } catch (e: unknown) {
      // K3: fremde/gelöschte Fälle → Fehlerkarte statt Absturz
      setDetail(null)
      if (e instanceof ApiErrorResponse && (e.statusCode === 403 || e.statusCode === 404)) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        setDetailError(serverDetail || 'Kein Zugriff auf diesen Fall.')
      } else {
        setDetailError(extractErrorMessage(e, { loadError: 'Fall konnte nicht geladen werden' }))
      }
    } finally {
      setDetailLoading(false)
    }
  }, [requestId])

  const loadTimeline = useCallback(async () => {
    if (!requestId) return
    setTimelineLoading(true)
    setTimelineError(null)
    try {
      const events = await directorApi.getTimeline(requestId)
      setTimeline(events)
      // Fallakte v3 (B2): Cooldown initial aus reminder_sent-Events ableiten,
      // damit der Button ohne 409-Klick korrekt disabled ist (24-h-Fenster).
      setRemindCooldownUntil(deriveCooldown(events, 'documents'))
      setInfoRemindCooldownUntil(deriveCooldown(events, 'info'))
    } catch {
      setTimelineError('Timeline konnte nicht geladen werden.')
    } finally {
      setTimelineLoading(false)
    }
  }, [requestId])

  useEffect(() => {
    // Fallakte v3 (A1): Notice nur bei Fallwechsel zurücksetzen — nicht bei
    // refreshKey (sonst frisst der Refresh nach einer Aktion die Meldung).
    setNotice(null)
    setErrorNotice(null)
    setRemindCooldownUntil(null)
    setInfoRemindCooldownUntil(null)
    setModal(null)
  }, [requestId])

  useEffect(() => {
    void loadDetail()
    void loadTimeline()
  }, [loadDetail, loadTimeline, refreshKey])

  const refreshAfterAction = useCallback(async () => {
    setModal(null)
    // onChanged bump parent's refreshKey → the effect reloads Detail+Timeline
    await onChanged()
  }, [onChanged])

  const openModal = useCallback((kind: Exclude<ActionModal, null>) => {
    setNotice(null)
    setErrorNotice(null)
    setModal(kind)
  }, [])

  const handleRequestDocs = async () => {
    if (!detail || requestingDocs || cancellingDocs) return
    setRequestingDocs(true)
    try {
      await directorApi.requestRequestDocuments(detail.id)
      // Fallakte v3 (A1): Refresh zuerst, Meldung danach — überlebt den Refresh.
      await loadDetail()
      await onChanged()
      setNotice('Dokumente angefordert — die Lehrperson wird per E-Mail informiert.')
    } catch (e: unknown) {
      // Fallakte v3 (B4): Fehler als Banner statt alert()
      if (e instanceof ApiErrorResponse) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        if (serverDetail) {
          setErrorNotice(serverDetail)
          setRequestingDocs(false)
          return
        }
      }
      setErrorNotice(extractErrorMessage(e, { loadError: 'Dokumenteanforderung fehlgeschlagen' }))
    } finally {
      setRequestingDocs(false)
    }
  }

  const handleCancelDocs = async () => {
    if (!detail || cancellingDocs || requestingDocs) return
    setCancellingDocs(true)
    setNotice(null)
    setErrorNotice(null)
    try {
      await directorApi.cancelRequestDocuments(detail.id)
      await loadDetail()
      await onChanged()
      setNotice('Dokumentenanfrage storniert — die Lehrperson muss nichts mehr einreichen.')
    } catch (e: unknown) {
      if (e instanceof ApiErrorResponse) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        if (serverDetail) {
          setErrorNotice(serverDetail)
          setCancellingDocs(false)
          return
        }
      }
      setErrorNotice(extractErrorMessage(e, { loadError: 'Storno der Dokumentenanfrage fehlgeschlagen' }))
    } finally {
      setCancellingDocs(false)
    }
  }

  const parseCooldownFromError = (message: string): string | null => {
    const match = message.match(/ab\s+(\d{4}-\d{2}-\d{2}T[\d:.+\-Z]+)/)
    return match?.[1] ?? null
  }

  const handleRemind = async (kind: 'documents' | 'info') => {
    if (!detail || reminding) return
    setReminding(true)
    try {
      const resp = await directorApi.remind(detail.id, { kind })
      if (kind === 'info') {
        setInfoRemindCooldownUntil(resp.next_possible_at)
      } else {
        setRemindCooldownUntil(resp.next_possible_at)
      }
      setNotice('Erinnerung gesendet.')
      await loadTimeline()
    } catch (e: unknown) {
      if (e instanceof ApiErrorResponse && e.statusCode === 409) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        const cooldown = serverDetail ? parseCooldownFromError(serverDetail) : null
        if (cooldown) {
          if (kind === 'info') setInfoRemindCooldownUntil(cooldown)
          else setRemindCooldownUntil(cooldown)
        }
        if (cooldown) {
          setNotice(serverDetail ?? 'Erinnerung derzeit nicht möglich.')
        } else {
          setErrorNotice(serverDetail ?? 'Erinnerung derzeit nicht möglich.')
        }
      } else {
        setErrorNotice(extractErrorMessage(e, { loadError: 'Erinnerung fehlgeschlagen' }))
      }
    } finally {
      setReminding(false)
    }
  }

  const handleAbortSearch = async () => {
    if (!detail || aborting) return
    setAborting(true)
    setNotice(null)
    setErrorNotice(null)
    try {
      await directorApi.abortSearch(detail.id)
      setNotice('Suche abgebrochen')
      await loadDetail()
      await onChanged()
    } catch (e: unknown) {
      setErrorNotice(extractErrorMessage(e, { loadError: 'Suche konnte nicht abgebrochen werden' }))
    } finally {
      setAborting(false)
    }
  }

  const handlePayerChange = async (paymentType: 'private' | 'school') => {
    const decisionId = detail?.latest_decision?.id
    if (!decisionId || !detail || payerSaving) return
    if (detail.latest_decision?.payment_type === paymentType) return
    setPayerSaving(true)
    setNotice(null)
    setErrorNotice(null)
    try {
      await directorApi.patchDecision(decisionId, { payment_type: paymentType })
      await loadDetail()
      await onChanged()
      setNotice(`Zahler geändert: ${PAYMENT_TYPE_LABELS[paymentType]}.`)
    } catch (e: unknown) {
      setErrorNotice(extractErrorMessage(e, { loadError: 'Zahler konnte nicht geändert werden' }))
    } finally {
      setPayerSaving(false)
    }
  }

  const handleDownload = async (docId: string, filename: string) => {
    if (!requestId) return
    try {
      const blob = await teacherPriorityApi.downloadRequestDocument(requestId, docId)
      const url = URL.createObjectURL(blob as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setErrorNotice('Download fehlgeschlagen')
    }
  }

  if (!requestId) {
    return (
      <div className="flex-1 min-w-0 flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400">
          <p className="text-sm font-medium">Fall auswählen</p>
          <p className="text-xs mt-1">Links einen Fall wählen, um die Fallakte zu öffnen.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-gray-50">
      {/* Aktions-/Statusleiste oben */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between gap-2 px-5 shrink-0">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="lg:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Zurück zur Liste"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {detail && (() => {
            const s = headerStatus(detail)
            return (
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${s.classes}`}>
                {s.label}
              </span>
            )
          })()}
          {detail && (
            <span className="text-xs text-gray-400 truncate">
              Fall #{requestId.slice(0, 4).toUpperCase()} · eingereicht{' '}
              {detail.created_at
                ? new Date(detail.created_at).toLocaleDateString('de-CH')
                : '—'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1"><ChevronLeft className="w-3.5 h-3.5" /> Vorheriger</span>
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1">Nächster <ChevronRight className="w-3.5 h-3.5" /></span>
          </button>
        </div>
      </div>

      {/* Fehler-/Ladezustand */}
      {detailError && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-sm bg-white border border-red-200 rounded-xl p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-900 mb-1">Fall nicht verfügbar</p>
            <p className="text-xs text-gray-500 mb-4">{detailError}</p>
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800"
            >
              Zurück zur Liste
            </button>
          </div>
        </div>
      )}

      {!detailError && detailLoading && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-gray-400">Fallakte wird geladen…</p>
        </div>
      )}

      {!detailError && !detailLoading && detail && (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4 max-w-4xl w-full">
            {notice && (
              <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2" role="status">
                {notice}
              </div>
            )}
            {errorNotice && (
              <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2" role="alert">
                {errorNotice}
              </div>
            )}

            {readOnly && (
              <div className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                  <span>
                    {detail.status === 'booked'
                      ? 'Fall gebucht — Genehmigt/Abgelehnt gesperrt.'
                      : 'Fall entschieden — Entscheidung kann hier geändert werden.'}
                  </span>
                </div>
                {detail.latest_decision && (
                  <div className="flex flex-wrap items-center gap-2 pl-6">
                    <span className="font-semibold">
                      {DECISION_TYPE_LABELS[detail.latest_decision.decision_type]
                        || detail.latest_decision.decision_type}
                    </span>
                    {detail.latest_decision.decision_type === 'approved' && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span>Zahler</span>
                        {config?.can_decide_payer === false ? (
                          <span className="font-semibold">
                            {PAYMENT_TYPE_LABELS[detail.latest_decision.payment_type]
                              || detail.latest_decision.payment_type}
                          </span>
                        ) : (
                          <span className="inline-flex gap-3">
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                              <input
                                type="radio"
                                name="case-payer"
                                checked={detail.latest_decision.payment_type === 'private'}
                                disabled={payerSaving}
                                onChange={() => { void handlePayerChange('private') }}
                              />
                              Privat
                            </label>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                              <input
                                type="radio"
                                name="case-payer"
                                checked={detail.latest_decision.payment_type === 'school'}
                                disabled={payerSaving}
                                onChange={() => { void handlePayerChange('school') }}
                              />
                              Schule
                            </label>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Offene Rückfrage (Info-Reminder-Kontext, K4) */}
            {detail.latest_decision?.decision_type === 'info_requested' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0">
                  <MessageCircle className="w-3.5 h-3.5" />
                </span>
                <div className="text-sm text-blue-900 leading-relaxed flex-1 min-w-0">
                  <p className="font-semibold">Rückfrage offen</p>
                  {detail.latest_decision.info_request_note && (
                    <p className="text-xs mt-0.5">«{detail.latest_decision.info_request_note}»</p>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleRemind('info')}
                      disabled={reminding || Boolean(infoRemindCooldownUntil) || config?.can_info_request === false}
                      title={infoRemindCooldownUntil
                        ? `Erinnerung bereits gesendet — nächste möglich ab ${formatDeadline(infoRemindCooldownUntil)}`
                        : 'Erinnerungs-E-Mail senden (max. alle 24 h)'}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BellRing className="w-3.5 h-3.5" />
                      {infoRemindCooldownUntil
                        ? `möglich ab ${formatDeadline(infoRemindCooldownUntil)}`
                        : 'Erinnerung senden'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Fall-Kopf */}
            <div className="flex items-start gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                style={{ backgroundColor: avatarColorOf(detail.requesting_teacher_name) }}
                aria-hidden
              >
                {initialsOf(detail.requesting_teacher_name)}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold truncate">{detail.requesting_teacher_name}</h1>
                <p className="text-sm text-gray-500 truncate">
                  {detail.lesson_count} Lektionen
                </p>
              </div>
            </div>

            <CaseInfoLine detail={detail} config={config} />
            <CaseFactsGrid detail={detail} />
            {detail.requesting_teacher_id ? (
              <EffectivePlanView
                teacherId={detail.requesting_teacher_id}
                directory="none"
                caseWeeks={caseWeekKeys(detail.lessons)}
                reloadKey={(refreshKey ?? 0) + planTick}
                actions={plan => (
                  <CasePlanActions
                    detail={detail}
                    deviations={plan?.deviations ?? []}
                    readOnly={readOnly}
                    onChanged={async () => { await loadDetail(); setPlanTick(t => t + 1); await onChanged() }}
                  />
                )}
              />
            ) : null}
            <CaseLessonsTable detail={detail} />
            <CaseDocumentsBlock
              detail={detail}
              canRequestDocs={config?.can_request_documents !== false && !readOnly}
              isRequestingDocs={requestingDocs}
              onRequestDocs={handleRequestDocs}
              isCancellingDocs={cancellingDocs}
              onCancelDocs={handleCancelDocs}
              onPreview={setPreviewDoc}
              onDownload={d => handleDownload(d.id, d.filename)}
              onRemind={() => handleRemind('documents')}
              remindCooldownUntil={remindCooldownUntil}
              isReminding={reminding}
            />
            <CaseTimelineBlock
              events={timeline}
              loading={timelineLoading}
              error={timelineError}
              onRetry={loadTimeline}
            />
          </div>

          <CaseActionFooter
            config={config}
            readOnly={readOnly}
            reviseDecision={
              readOnly
              && ['approved', 'rejected', 'returned'].includes(
                detail.latest_decision?.decision_type ?? '',
              )
            }
            booked={detail.status === 'booked'}
            abortSearch={
              !caseSentenceAbortVisible(detail, readOnly)
              && (!!detail.gate_skipped_by_admin || (!!detail.created_on_behalf && detail.status !== 'director_pending' && detail.status !== 'director_returned'))
            }
            onApprove={() => openModal('approve')}
            onReject={() => openModal('reject')}
            onAbortSearch={handleAbortSearch}
            onInfo={() => openModal('info')}
            onReturn={() => openModal('return')}
            onChat={() => navigate(`/teacher/chats?selected=${detail.id}`)}
          />
        </>
      )}

      {/* Modals */}
      {detail && (
        <>
          <QuickApproveModal
            isOpen={modal === 'approve'}
            item={detail}
            config={config}
            lessons={detail.lessons}
            initialPaymentType={
              detail.latest_decision?.decision_type === 'approved'
                ? (detail.latest_decision.payment_type as 'private' | 'school')
                : undefined
            }
            onClose={() => setModal(null)}
            onSuccess={refreshAfterAction}
            onConflict={() => { setModal(null); void onChanged() }}
          />
          <QuickRejectModal
            isOpen={modal === 'reject'}
            item={detail}
            config={config}
            onClose={() => setModal(null)}
            onSuccess={refreshAfterAction}
            onConflict={() => { setModal(null); void onChanged() }}
          />
          <QuickInfoModal
            isOpen={modal === 'info'}
            item={detail}
            onClose={() => setModal(null)}
            onSuccess={refreshAfterAction}
            onConflict={() => { setModal(null); void onChanged() }}
          />
          <QuickReturnModal
            isOpen={modal === 'return'}
            item={detail}
            lessons={detail.lessons}
            onClose={() => setModal(null)}
            onSuccess={refreshAfterAction}
            onConflict={() => { setModal(null); void onChanged() }}
          />
        </>
      )}

      {/* E8/EC3: Dokument-Vorschau (PDF/Bild) — neue documents-API */}
      {previewDoc && requestId && (
        <DocumentPreviewModal
          isOpen
          onClose={() => setPreviewDoc(null)}
          documents={detail?.documents ?? [previewDoc]}
          initialDocumentId={previewDoc.id}
          fetchFile={docId => teacherPriorityApi.downloadRequestDocument(requestId, docId)}
          onDownload={(docId, filename) => handleDownload(docId, filename)}
        />
      )}
    </div>
  )
}
